<script setup>
import { Head, Link, router } from '@inertiajs/vue3'
import { ref, computed } from 'vue'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import { Button } from '@/Components/ui/button'
import { Card } from '@/Components/ui/card'
import { Badge } from '@/Components/ui/badge'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/Components/ui/alert-dialog'
import '@mux/mux-player'
import teacher from '@/routes/teacher'
import CommentSection from '@/Components/CommentSection.vue'
import axios from 'axios'

const props = defineProps({
  content: Object,
  documentUrl: String,
  attachmentUrls: Array,
  relatedContent: Array,
})

const isVideo = computed(() => props.content.type === 1 || props.content.type?.value === 1)
const isDocument = computed(() => props.content.type === 2 || props.content.type?.value === 2)

const isFree = ref(props.content.is_free ?? false)
const toggling = ref(false)

// PDF modal state
const showPdfModal = ref(false)
const pdfModalUrl = ref('')
const pdfModalName = ref('')

const openPdfModal = (url, name) => {
  pdfModalUrl.value = url
  pdfModalName.value = name || 'Document'
  showPdfModal.value = true
}

const closePdfModal = () => {
  showPdfModal.value = false
  pdfModalUrl.value = ''
  pdfModalName.value = ''
}

const getStatusColor = (status) => {
  switch (status) {
    case 'published': return 'bg-green-500 hover:bg-green-600'
    case 'draft': return 'bg-gray-500 hover:bg-gray-600'
    case 'archived': return 'bg-amber-500 hover:bg-amber-600'
    default: return 'bg-gray-500'
  }
}

const getStatusLabel = (status) => {
  switch (status) {
    case 'published': return 'Published'
    case 'draft': return 'Draft'
    case 'archived': return 'Archived'
    default: return status
  }
}

const contentTypeLabel = computed(() => isDocument.value ? 'Document' : 'Video')

const deleteContent = () => {
  router.delete(teacher.content.destroy(props.content.id).url)
}

const toggleFree = async () => {
  if (toggling.value) return

  toggling.value = true
  try {
    const response = await axios.post(teacher.content.toggleFree(props.content.id).url)
    isFree.value = response.data.is_free

    alert(response.data.message)
  } catch (error) {
    console.error('Failed to toggle free status:', error)
    alert('Failed to update status. Please try again.')
  } finally {
    toggling.value = false
  }
}
</script>

<template>
  <Head :title="content.title" />

  <DashboardLayout>
    <div class="flex py-6 px-4 md:px-8 font-sans">
      <div class="w-full flex flex-col gap-6">
        <!-- Breadcrumb -->
        <nav class="flex items-center gap-2 text-sm text-gray-500">
          <Link :href="teacher.content.index().url" class="font-medium hover:underline hover:text-gray-700">
            My Content
          </Link>
          <span class="font-medium">/</span>
          <span class="text-gray-900 font-medium">{{ content.title }}</span>
        </nav>

        <div class="flex flex-col lg:flex-row gap-8">
          <!-- Main Content Area -->
          <div class="flex-1 space-y-6">
            <!-- Video Player (for video content) -->
            <div v-if="isVideo" class="bg-black rounded-2xl overflow-hidden shadow-xl aspect-video w-full">
              <mux-player
                v-if="content.mux_playback_id"
                stream-type="on-demand"
                :playback-id="content.mux_playback_id"
                :metadata-video-title="content.title"
                primary-color="#2563EB"
                class="w-full h-full"
              ></mux-player>
              <div v-else class="w-full h-full flex flex-col items-center justify-center text-gray-400">
                <span class="material-symbols-outlined text-5xl mb-2">movie</span>
                <span>Video processing or unavailable</span>
              </div>
            </div>

            <!-- Document Viewer (for document content) -->
            <div v-if="isDocument">
              <div v-if="documentUrl">
                <button
                  @click="openPdfModal(documentUrl, content.attachments?.[0]?.original_name || 'document.pdf')"
                  class="w-full rounded-2xl overflow-hidden shadow-xl border border-gray-200 hover:border-blue-300 transition-colors group cursor-pointer"
                >
                  <!-- PDF preview -->
                  <div class="w-full h-150 relative">
                    <iframe
                      :src="documentUrl"
                      class="w-full h-full pointer-events-none"
                      frameborder="0"
                      tabindex="-1"
                    />
                    <!-- Overlay on hover -->
                    <div class="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <span class="material-symbols-outlined text-4xl text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg">open_in_full</span>
                    </div>
                  </div>
                  <!-- File info bar -->
                  <div class="flex items-center justify-between px-5 py-3 bg-gray-50 border-t border-gray-200">
                    <div class="flex items-center gap-3 min-w-0">
                      <span class="material-symbols-outlined text-red-500 text-xl shrink-0">picture_as_pdf</span>
                      <span class="text-sm font-medium text-gray-700 truncate">{{ content.attachments?.[0]?.original_name || 'document.pdf' }}</span>
                    </div>
                    <span class="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-blue-600 bg-blue-50 group-hover:bg-blue-100 rounded-lg transition-colors shrink-0">
                      <span class="material-symbols-outlined text-base">open_in_full</span>
                      View Document
                    </span>
                  </div>
                </button>
              </div>
              <div v-else class="w-full h-64 flex flex-col items-center justify-center text-gray-400 bg-gray-50 rounded-2xl border border-gray-200">
                <span class="material-symbols-outlined text-5xl mb-2">description</span>
                <span>Document unavailable</span>
              </div>
            </div>

            <!-- Title + Action Buttons -->
            <div class="flex items-start justify-between gap-4">
              <div class="flex-1">
                <h1 class="text-2xl md:text-3xl font-bold text-gray-900">{{ content.title }}</h1>
              </div>
              <div class="flex gap-2 shrink-0">
                <a :href="`/library/${content.slug}`" target="_blank" rel="noopener noreferrer" title="View on Public Library">
                  <Button variant="outline" size="icon">
                    <span class="material-symbols-outlined text-lg">open_in_new</span>
                  </Button>
                </a>

                <!-- Toggle Free Button -->
                <Button
                  @click="toggleFree"
                  :variant="isFree ? 'default' : 'outline'"
                  size="icon"
                  :disabled="toggling"
                  :title="isFree ? `${contentTypeLabel} is Free - Click to require subscription` : `${contentTypeLabel} requires subscription - Click to make free`"
                >
                  <span class="material-symbols-outlined text-lg">{{ isFree ? 'lock_open' : 'lock' }}</span>
                </Button>

                <Link :href="teacher.content.edit(content.id)" title="Edit Content">
                  <Button variant="outline" size="icon">
                    <span class="material-symbols-outlined text-lg">edit</span>
                  </Button>
                </Link>

                <!-- Delete Button with AlertDialog -->
                <AlertDialog>
                  <AlertDialogTrigger as-child>
                    <Button variant="destructive" size="icon" title="Delete Content">
                      <span class="material-symbols-outlined text-lg">delete</span>
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This action cannot be undone. This will permanently delete your content
                        and remove all associated data from our servers.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction @click="deleteContent">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>

            <!-- Content Type Badge -->
            <div class="flex items-center gap-3">
              <span
                class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                :class="isDocument ? 'bg-orange-100 text-orange-700 border border-orange-200' : 'bg-blue-100 text-blue-700 border border-blue-200'"
              >
                <span class="material-symbols-outlined text-sm">{{ isDocument ? 'description' : 'videocam' }}</span>
                {{ contentTypeLabel }}
              </span>
              <Badge :class="getStatusColor(content.status)">{{ getStatusLabel(content.status) }}</Badge>
            </div>

            <!-- Supporting Documents (for video content with attachments) -->
            <div v-if="isVideo && attachmentUrls && attachmentUrls.length > 0" class="bg-white rounded-xl border border-gray-200 p-4">
              <h3 class="text-sm font-bold text-gray-900 flex items-center gap-2 mb-3">
                <span class="material-symbols-outlined text-lg text-gray-600">attach_file</span>
                Supporting Documents
              </h3>
              <div class="space-y-2">
                <button
                  v-for="(attachment, idx) in attachmentUrls"
                  :key="idx"
                  @click="openPdfModal(attachment.url, attachment.original_name)"
                  class="w-full flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 hover:bg-blue-50 hover:border-blue-200 transition-colors text-left group"
                >
                  <span class="material-symbols-outlined text-red-500 text-xl shrink-0">picture_as_pdf</span>
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-medium text-gray-900 truncate group-hover:text-blue-700">{{ attachment.original_name || 'document.pdf' }}</p>
                    <p v-if="attachment.size" class="text-xs text-gray-500">{{ (attachment.size / (1024 * 1024)).toFixed(1) }} MB</p>
                  </div>
                  <span class="material-symbols-outlined text-gray-400 group-hover:text-blue-600 text-lg shrink-0">open_in_full</span>
                </button>
              </div>
            </div>

            <!-- Metadata Badges with Colored Icons -->
            <div class="flex flex-wrap justify-between gap-4 py-4">
                        <div class="flex items-center gap-3">
                            <div class="size-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                                <span class="material-symbols-outlined text-base text-blue-600">book</span>
                            </div>
                            <div>
                                <div class="text-xs text-gray-500 font-medium">Subject</div>
                                <div class="text-sm font-bold" :class="content.subject ? 'text-gray-900' : 'text-gray-400 italic'">
                                    {{ content.subject?.name || 'Not set' }}
                                </div>
                            </div>
                        </div>

                        <div class="flex items-center gap-3">
                            <div class="size-8 rounded-full bg-purple-50 flex items-center justify-center shrink-0">
                                <span class="material-symbols-outlined text-base text-purple-600">assignment</span>
                            </div>
                            <div>
                                <div class="text-xs text-gray-500 font-medium">Exam Board</div>
                                <div class="text-sm font-bold" :class="content.examboard ? 'text-gray-900' : 'text-gray-400 italic'">
                                    {{ content.examboard?.name || 'Not set' }}
                                </div>
                            </div>
                        </div>

                        <div class="flex items-center gap-3">
                            <div class="size-8 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                                <span class="material-symbols-outlined text-base text-emerald-600">trending_up</span>
                            </div>
                            <div>
                                <div class="text-xs text-gray-500 font-medium">Level</div>
                                <div class="text-sm font-bold" :class="content.level ? 'text-gray-900' : 'text-gray-400 italic'">
                                    {{ content.level?.name || 'Not set' }}
                                </div>
                            </div>
                        </div>

                        <div class="flex items-center gap-3">
                            <div class="size-8 rounded-full flex items-center justify-center shrink-0" :class="content.status === 'published' ? 'bg-green-50' : content.status === 'draft' ? 'bg-gray-50' : 'bg-amber-50'">
                                <span class="material-symbols-outlined text-base" :class="content.status === 'published' ? 'text-green-600' : content.status === 'draft' ? 'text-gray-600' : 'text-amber-600'">{{ content.status === 'published' ? 'check_circle' : content.status === 'draft' ? 'edit_note' : 'archive' }}</span>
                            </div>
                            <div>
                                <div class="text-xs text-gray-500 font-medium">Status</div>
                                <div class="text-sm font-bold text-gray-900">{{ getStatusLabel(content.status) }}</div>
                            </div>
                        </div>
            </div>

            <!-- Stats Grid -->
            <div class="flex flex-wrap justify-between gap-6 py-4 border-y border-gray-100">
                       <div class="text-center">
                           <div class="text-xs text-gray-500 font-medium mb-1">Upload Date</div>
                           <div class="text-sm font-bold text-gray-900">{{ content.created_at_human }}</div>
                       </div>
                       <div class="text-center">
                           <div class="text-xs text-gray-500 font-medium mb-1">Total Views</div>
                           <div class="text-sm font-bold text-gray-900">{{ content.view_count || 0 }}</div>
                       </div>
                       <div class="text-center">
                           <div class="text-xs text-gray-500 font-medium mb-1">Avg Rating</div>
                           <div class="text-sm font-bold text-gray-900 flex items-center justify-center gap-1">
                               <span class="material-symbols-outlined text-yellow-500 text-base">star</span>
                               {{ content.getAverageRating || '0.0' }}
                           </div>
                       </div>
                       <div class="text-center">
                           <div class="text-xs text-gray-500 font-medium mb-1">Comments</div>
                           <div class="text-sm font-bold text-gray-900">{{ content.comments_count || 0 }}</div>
                       </div>
            </div>

            <!-- Topics -->
            <div class="flex flex-wrap items-center gap-4 py-4">
                       <h2 class="text-sm font-bold text-gray-900 flex items-center gap-2">
                           <span class="material-symbols-outlined text-lg text-gray-600">local_offer</span>
                           Topics
                       </h2>
                       <div class="flex flex-wrap gap-2">
                           <span v-for="(topic, idx) in content.topics" :key="idx"
                                 class="px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-lg border border-blue-100 hover:bg-blue-100 transition-colors cursor-pointer">
                               #{{ topic }}
                           </span>
                           <span v-if="!content.topics || content.topics.length === 0" class="text-gray-400 italic text-sm">No topics added</span>
                       </div>
            </div>

            <!-- Comments Section -->
            <div class="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                   <CommentSection :content-id="content.id" :content-owner-id="content.teacher_id" />
            </div>
           </div>

           <!-- Sidebar -->
           <div class="w-full lg:w-80 space-y-6 shrink-0">
               <div class="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                   <h3 class="font-bold text-gray-900 mb-4 flex items-center gap-2">
                       <span class="material-symbols-outlined text-xl text-gray-600">library_books</span>
                       Related Content
                   </h3>
                   <div class="space-y-4">
                       <Link v-for="related in relatedContent" :key="related.id"
                             :href="teacher.content.show(related.id)"
                             class="flex gap-3 group hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors">
                           <!-- Video thumbnail -->
                           <div v-if="related.type === 1 || related.type?.value === 1" class="w-28 h-20 bg-gray-100 rounded-lg overflow-hidden relative shrink-0">
                               <img v-if="related.thumbnail_url" :src="related.thumbnail_url" :alt="related.title" class="absolute inset-0 w-full h-full object-cover">
                               <div v-else class="w-full h-full flex items-center justify-center text-white/70">
                                   <span class="material-symbols-outlined text-xl">movie</span>
                               </div>
                               <div v-if="related.duration" class="absolute bottom-1 right-1 bg-black/80 backdrop-blur-sm text-white text-[10px] px-1.5 py-0.5 rounded">
                                   {{ related.formatted_duration || related.duration + 's' }}
                               </div>
                           </div>
                           <!-- Document thumbnail -->
                           <div v-else class="w-28 h-20 bg-orange-50 border border-orange-200 rounded-lg overflow-hidden relative shrink-0 flex flex-col items-center justify-center">
                               <span class="material-symbols-outlined text-2xl text-orange-500">picture_as_pdf</span>
                               <span class="text-[10px] font-semibold text-orange-600 mt-0.5">PDF</span>
                           </div>
                           <div class="flex-1 min-w-0">
                               <h4 class="text-sm font-semibold text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors mb-1">
                                   {{ related.title }}
                               </h4>
                               <div class="flex items-center gap-2 text-xs text-gray-500">
                                   <span class="flex items-center gap-1">
                                       <span class="material-symbols-outlined text-xs">visibility</span>
                                       {{ related.view_count || 0 }}
                                   </span>
                                   <span v-if="related.subject" class="flex items-center gap-1">
                                       <span class="material-symbols-outlined text-xs">book</span>
                                       {{ related.subject.name }}
                                   </span>
                               </div>
                           </div>
                       </Link>
                       <div v-if="relatedContent.length === 0" class="text-sm text-gray-400 text-center py-8">
                           No related content yet.
                       </div>
                   </div>
               </div>
           </div>
        </div>
      </div>
    </div>

    <!-- Immersive PDF Modal -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-200"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showPdfModal"
          class="fixed inset-0 z-50 flex flex-col bg-media/95 backdrop-blur-sm"
        >
          <!-- Modal Header -->
          <div class="flex items-center justify-between px-6 py-3 bg-media border-b border-white/15">
            <div class="flex items-center gap-3 min-w-0">
              <span class="material-symbols-outlined text-red-400 text-xl shrink-0">picture_as_pdf</span>
              <span class="text-white font-medium text-sm truncate">{{ pdfModalName }}</span>
            </div>
            <div class="flex items-center gap-2">
              <a
                :href="pdfModalUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white/70 hover:text-white bg-media-2 hover:bg-media-2 rounded-lg transition-colors"
              >
                <span class="material-symbols-outlined text-sm">open_in_new</span>
                Open in New Tab
              </a>
              <button
                @click="closePdfModal"
                class="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-white hover:bg-media-2 rounded-lg transition-colors"
              >
                <span class="material-symbols-outlined text-xl">close</span>
              </button>
            </div>
          </div>

          <!-- PDF Content -->
          <div class="flex-1 p-4">
            <iframe
              :src="pdfModalUrl"
              class="w-full h-full rounded-lg"
              frameborder="0"
            />
          </div>
        </div>
      </Transition>
    </Teleport>
  </DashboardLayout>
</template>
